import {
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 0x10
const MEMORY_SIZE = 0x0400
const CHANNEL_START = 0x0010
const CHANNEL_COUNT = 16
const WRITE_END = 0x0190
const PASSWORD = new TextEncoder().encode('MXT8KCUMHS1X7BN/')

export type RT1Profile = {
  protocol: 'retevis-rt1'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: number
  channelMinimum: 1
  channelMaximum: number
  nameLength: 0
  memorySize: number
  powerLevels: readonly ['Low', 'High']
  identityPrefix: string
}

export const RT1_PROFILES: readonly RT1Profile[] = [
  {
    protocol: 'retevis-rt1',
    id: 'retevis-rt1',
    vendor: 'Retevis',
    model: 'RT1',
    summary: 'Retevis RT1 VHF/UHF commercial handheld.',
    tags: ['Experimental', 'Read + write', '16 channels', 'No channel names'],
    baudRate: 2400,
    channelMinimum: 1,
    channelMaximum: CHANNEL_COUNT,
    nameLength: 0,
    memorySize: MEMORY_SIZE,
    powerLevels: ['Low', 'High'],
    identityPrefix: 'PXT8K',
  },
  {
    protocol: 'retevis-rt1',
    id: 'retevis-rt26',
    vendor: 'Retevis',
    model: 'RT26',
    summary: 'Retevis RT26 compact business radio.',
    tags: ['Experimental', 'Read + write', '16 channels', 'No channel names'],
    baudRate: 4800,
    channelMinimum: 1,
    channelMaximum: 16,
    nameLength: 0,
    memorySize: MEMORY_SIZE,
    powerLevels: ['Low', 'High'],
    identityPrefix: 'PDK80',
  },
]

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function decodeBbcd(raw: Uint8Array): number | null {
  if (raw.every((value) => value === 0xff)) return null
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    const byte = raw[index]
    const high = byte >> 4
    const low = byte & 0x0f
    if (high > 9 || low > 9) return null
    value = value * 100 + high * 10 + low
  }
  return value || null
}

function encodeBbcd(value: number, length: number): Uint8Array {
  const result = new Uint8Array(length)
  let remaining = Math.max(0, Math.trunc(value))
  for (let index = 0; index < length; index += 1) {
    const part = remaining % 100
    result[index] = (Math.floor(part / 10) << 4) | (part % 10)
    remaining = Math.floor(remaining / 100)
  }
  return result
}

function decodeTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  if (raw.every((value) => value === 0xff)) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    value = value * 100 + (raw[index] >> 4) * 10 + (raw[index] & 0x0f)
  }
  if (value >= 12000) {
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: value - 12000,
      dtcsPolarity: 'R',
    }
  }
  if (value >= 8000) {
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: value - 8000,
      dtcsPolarity: 'N',
    }
  }
  return {
    toneMode: 'ctcss',
    ctcss: value / 10,
    dtcs: null,
    dtcsPolarity: 'N',
  }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  if (channel.toneMode === 'none') return [0xff, 0xff]
  let value = 0
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    value =
      channel.dtcs +
      (channel.dtcsPolarity === 'R' ? 12000 : 8000)
  }
  return [value % 100, Math.floor(value / 100)]
    .map(
      (part) =>
        (((Math.floor(part / 10) & 0x0f) << 4) |
          (part % 10)) &
        0xff,
    ) as unknown as readonly [number, number]
}

export function parseRT1Channel(
  image: Uint8Array,
  profile: RT1Profile,
  number: number,
): WebChannel {
  const start = CHANNEL_START + (number - 1) * BLOCK_SIZE
  const raw = image.slice(start, start + BLOCK_SIZE)
  if (raw.byteLength !== BLOCK_SIZE || raw[0] === 0xff) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeBbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const txRaw = raw.slice(4, 8)
  const txFrequency = txRaw.every((value) => value === 0xff)
    ? null
    : decodeBbcd(txRaw)
  const txTone = decodeTone(raw.slice(8, 10))
  const rxTone = decodeTone(raw.slice(10, 12))
  const selected = txTone.toneMode === 'none' ? rxTone : txTone
  return {
    number,
    empty: false,
    name: '',
    rxFrequency: rxFrequency * 10,
    txFrequency: txFrequency ? txFrequency * 10 : null,
    toneMode: selected.toneMode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.dtcsPolarity,
    mode: raw[12] & 0x20 ? 'FM' : 'NFM',
    power: raw[12] & 0x80 ? 'High' : 'Low',
    skip: (raw[12] & 0x10) !== 0,
  }
}

export function parseRT1Channels(
  image: Uint8Array,
  profile: RT1Profile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseRT1Channel(image, profile, index + 1),
  )
}

export function applyRT1Channel(
  image: Uint8Array,
  profile: RT1Profile,
  channel: WebChannel,
): void {
  const start = CHANNEL_START + (channel.number - 1) * BLOCK_SIZE
  const raw = image.subarray(start, start + BLOCK_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  raw.fill(0)
  encodeBbcd(Math.round(channel.rxFrequency / 10), 4).forEach(
    (value, index) => {
      raw[index] = value
    },
  )
  if (channel.txFrequency) {
    encodeBbcd(Math.round(channel.txFrequency / 10), 4).forEach(
      (value, index) => {
        raw[4 + index] = value
      },
    )
  } else {
    raw.fill(0xff, 4, 8)
  }
  const tone = encodeTone(channel)
  raw.set(tone, 8)
  raw.set(tone, 10)
  raw[12] =
    (channel.skip ? 0x10 : 0) |
    (channel.mode === 'FM' ? 0x20 : 0) |
    (channel.power === 'High' ? 0x80 : 0)
}

async function enterProgrammingMode(
  profile: RT1Profile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  for (const magic of ['PROGRAMa', 'PROGRAMb']) {
    await transport.write(new TextEncoder().encode(magic))
    if ((await transport.readExact(1, 1500))[0] !== ACK) {
      throw new Error('The radio refused RT1 programming mode.')
    }
  }
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(16, 1800)
  if (
    !new TextDecoder()
      .decode(identity.slice(0, 5))
      .startsWith(profile.identityPrefix)
  ) {
    throw new Error('The connected radio does not identify as Retevis RT1.')
  }
  await transport.write(PASSWORD)
  if ((await transport.readExact(1, 1800))[0] !== 0xb2) {
    throw new Error('The radio rejected the RT1 programming password.')
  }
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 1500))[0] !== ACK) {
    throw new Error('The radio refused the RT1 clone session.')
  }
}

async function exitProgrammingMode(
  transport: RadioTransport,
): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
}

function frame(command: number, address: number): Uint8Array {
  return Uint8Array.of(
    command,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  )
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  const command = frame(0x52, address)
  await transport.write(command)
  const response = await transport.readExact(BLOCK_SIZE + 4, 2500)
  if (
    response[0] !== 0x57 ||
    !command.slice(1).every((value, index) => response[index + 1] === value)
  ) {
    throw new Error(`Unexpected RT1 read reply at 0x${address.toString(16)}.`)
  }
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 1500))[0] !== ACK) {
    throw new Error(`The radio rejected RT1 block 0x${address.toString(16)}.`)
  }
  return response.slice(4)
}

export async function readRT1RadioWithTransport(
  profile: RT1Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE).fill(0xff)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (
      let address = 0;
      address < MEMORY_SIZE;
      address += BLOCK_SIZE
    ) {
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: address / BLOCK_SIZE + 1,
        total,
        label: 'Reading Retevis RT1 memory',
      })
    }
    return { image, reportedModel: 'RT1' }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeRT1RadioWithTransport(
  profile: RT1Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than RT1 memory.')
  }
  await enterProgrammingMode(profile, transport)
  try {
    const total = WRITE_END / BLOCK_SIZE
    for (let address = 0; address < WRITE_END; address += BLOCK_SIZE) {
      const packet = new Uint8Array(4 + BLOCK_SIZE)
      packet.set(frame(0x57, address))
      packet.set(image.slice(address, address + BLOCK_SIZE), 4)
      await transport.write(packet)
      if ((await transport.readExact(1, 1800))[0] !== ACK) {
        throw new Error(
          `The radio rejected RT1 write block 0x${address.toString(16)}.`,
        )
      }
      onProgress?.({
        current: address / BLOCK_SIZE + 1,
        total,
        label: 'Writing Retevis RT1 memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport)
  }
}
